import React, { useMemo } from 'react';

import { Box, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import type { ActiveRopaReviewMetrics } from 'config/api/ropaReviews/ropaReviews.types';
import processing_activities_messages from 'messages/processing_activities_messages';

import useStyles from '../ProcessingActivitiesAssetReviewsPage.styles';

type Props = {
  includedAssetsMessageKey?: 'included_assets' | 'owner_included_assets';
  metrics?: ActiveRopaReviewMetrics;
};

const AssetRopaMetricsSection: React.FC<Props> = ({ includedAssetsMessageKey = 'included_assets', metrics }) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const metricItems = useMemo(
    () => [
      { key: 'includedAssetsCount' as const, label: t(processing_activities_messages.asset_ropa.metrics[includedAssetsMessageKey]) },
      { key: 'awaitingResponseCount' as const, label: t(processing_activities_messages.asset_ropa.metrics.awaiting_response) },
      { key: 'submittedCount' as const, label: t(processing_activities_messages.asset_ropa.metrics.submitted) },
      { key: 'acceptedCount' as const, label: t(processing_activities_messages.asset_ropa.metrics.accepted) },
      {
        key: 'clarificationRequestedCount' as const,
        label: t(processing_activities_messages.asset_ropa.metrics.clarification_requested),
      },
      {
        key: 'assetsWithWarningsCount' as const,
        label: t(processing_activities_messages.asset_ropa.metrics.assets_with_warnings),
      },
    ],
    [includedAssetsMessageKey, t],
  );

  if (!metrics) return null;

  return (
    <Box aria-label={t(processing_activities_messages.asset_ropa.metrics.aria_label)} className={styles.metricsRow} component='section'>
      {metricItems.map(item => {
        const value = metrics[item.key];

        return (
          <Box key={item.key} aria-label={`${item.label}: ${value}`} className={styles.metricTile} role='group'>
            <Box className={styles.metricCard}>
              <Typography className={styles.metricLabel} color='textSecondary' variant='body2'>
                {item.label}
              </Typography>
              <div className={styles.metricValueLine}>
                <Typography className={styles.metricValue} component='div' variant='h4'>
                  {value}
                </Typography>
              </div>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default AssetRopaMetricsSection;
